// Os cards de TENDÊNCIA — e a razão de eles mandarem na hierarquia.
//
// "Um score diário não significa nada; uma tendência de 5 dias significa
// tudo." A tela fazia o contrário: o 92 do dia dominava a dobra e a média de
// 5,8h aparecia dentro de um parágrafo. A pesquisa tem nome para isso —
// ORTOSSONIA: a nota diária piora o sono de quem a lê. O score FICA; quem
// passa a mandar visualmente é a tendência.
//
// ⚠️ TODO NÚMERO AQUI VEM COM O DENOMINADOR NA MESMA LINHA. É a regra que o
// "-15h28min deficit vs 8h target" violava: número sem janela não é
// informação, é susto. As funções puras (lib/sleep/trends.ts) devolvem
// `days` e `nights` junto do valor exatamente para a tela não ter como
// esquecer.

import React from "react";
import { motion } from "framer-motion";
import { Clock, TrendingUp, TrendingDown, Minus, Activity } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import {
  deltaPct,
  hrvTrend,
  previousWindowStats,
  scheduleConsistency,
  windowStats,
  type TrendNight,
} from "@/lib/sleep/trends";

const ACCENT = "#00e5ff";

/** 7,5 → "7h30". Local: o formatter da página não é exportado. */
function fmtHours(h: number): string {
  const total = Math.round(h * 60);
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return mm === 0 ? `${hh}h` : `${hh}h${String(mm).padStart(2, "0")}`;
}

function Delta({ pct }: { pct: number | null }) {
  const { t } = useLanguage();
  if (pct === null) {
    return (
      <span className="flex items-center gap-1 text-[10px] font-display" style={{ color: "hsl(218 14% 50%)" }}>
        <Minus size={10} /> {t("sleep.trend.noBase")}
      </span>
    );
  }
  const up = pct > 0;
  const flat = Math.abs(pct) < 1;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  const color = flat ? "hsl(218 14% 55%)" : up ? "hsl(142 60% 55%)" : "hsl(38 92% 62%)";
  return (
    <span className="flex items-center gap-1 text-[10px] font-display font-semibold" style={{ color }}>
      <Icon size={11} />
      {flat ? "0%" : `${up ? "+" : ""}${pct}%`}
    </span>
  );
}

// ── 1 · A TENDÊNCIA, que passa a mandar ─────────────────────────────────────

interface TrendSummaryProps {
  nights: TrendNight[];
  now: number;
  /** Versão de uma linha, para a aba Log — acima do score do dia. */
  compact?: boolean;
}

export const SleepTrendSummary: React.FC<TrendSummaryProps> = ({ nights, now, compact = false }) => {
  const { t, tf } = useLanguage();
  const w7 = windowStats(nights, 7, now);
  const w30 = windowStats(nights, 30, now);

  // Os 7 dias ANTERIORES aos 7 atuais — a comparação que dá direção.
  const prev7 = previousWindowStats(nights, 7, now);
  const dur7Delta = deltaPct(w7.avgDuration, prev7.avgDuration);

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="rounded-2xl px-4 py-3 mb-4 flex items-center gap-3"
        style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 35% 6%))", border: "1px solid hsl(220 50% 45% / 0.30)" }}
      >
        <div className="min-w-0 flex-1">
          <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-0.5" style={{ color: "hsl(220 70% 70% / 0.70)" }}>
            {t("sleep.trend.avg7")}
          </p>
          {w7.avgDuration === null ? (
            <p className="font-display text-[15px] font-semibold" style={{ color: "hsl(218 14% 55%)" }}>
              {t("sleep.trend.noData")}
            </p>
          ) : (
            <div className="flex items-baseline gap-2">
              {/* O NÚMERO GRANDE da dobra é este — não o score do dia. */}
              <span className="font-display text-[26px] font-bold text-white leading-none">
                {fmtHours(w7.avgDuration)}
              </span>
              <Delta pct={dur7Delta} />
            </div>
          )}
          <p className="text-[9px] font-body mt-1" style={{ color: "hsl(218 14% 50%)" }}>
            {tf("sleep.trend.basis", { nights: String(w7.durationNights), days: String(w7.days) })}
          </p>
        </div>
      </motion.div>
    );
  }

  const rows = [
    { label: t("sleep.trend.avg7"), s: w7 },
    { label: t("sleep.trend.avg30"), s: w30 },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="rounded-2xl p-4 mb-4"
      style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 35% 6%))", border: "1px solid hsl(220 50% 45% / 0.30)" }}
    >
      <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-3" style={{ color: "hsl(220 70% 70% / 0.70)" }}>
        {t("sleep.trend.title")}
      </p>
      <div className="grid grid-cols-2 gap-3">
        {rows.map((r) => (
          <div key={r.label} className="rounded-xl p-3" style={{ background: "hsl(220 50% 12% / 0.45)", border: "1px solid hsl(220 50% 35% / 0.28)" }}>
            <p className="text-[9px] font-display uppercase tracking-wide mb-1" style={{ color: "hsl(218 14% 58%)" }}>
              {r.label}
            </p>
            <p className="font-display text-[20px] font-bold text-white leading-none">
              {r.s.avgDuration === null ? "—" : fmtHours(r.s.avgDuration)}
            </p>
            <p className="text-[9px] font-body mt-1" style={{ color: "hsl(218 14% 48%)" }}>
              {tf("sleep.trend.basis", { nights: String(r.s.durationNights), days: String(r.s.days) })}
            </p>
            {r.s.avgScore !== null && (
              <>
                <p className="text-[10px] font-display mt-1" style={{ color: ACCENT }}>
                  {t("sleep.score")} {r.s.avgScore}
                </p>
                <p className="text-[9px] font-body mt-1" style={{ color: "hsl(218 14% 48%)" }}>
                  {tf("sleep.scoreBasis", { nights: String(r.s.scoreNights), days: String(r.s.days) })}
                </p>
              </>
            )}
          </div>
        ))}
      </div>
    </motion.div>
  );
};

// ── 2 · CONSISTÊNCIA DE HORÁRIO ─────────────────────────────────────────────

export const ScheduleConsistencyCard: React.FC<{ nights: TrendNight[]; now: number }> = ({ nights, now }) => {
  const { t, tf } = useLanguage();
  const c = scheduleConsistency(nights, 30, now);
  const has = c.bedtimeStdDev !== null || c.wakeupStdDev !== null;

  const spread = (sd: number | null) => (sd === null ? "—" : `± ${fmtHours(sd)}`);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.05 }}
      className="rounded-2xl p-4 mb-4"
      style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 35% 6%))", border: "1px solid hsl(265 60% 60% / 0.22)" }}
    >
      <div className="flex items-center gap-1.5 mb-2">
        <Clock size={12} style={{ color: "hsl(265 60% 70%)" }} />
        <p className="text-[9px] font-display tracking-[0.18em] uppercase" style={{ color: "hsl(265 60% 72%)" }}>
          {t("sleep.consistency.title")}
        </p>
      </div>

      {has ? (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[9px] font-body" style={{ color: "hsl(218 14% 58%)" }}>{t("sleep.consistency.bedtime")}</p>
            <p className="font-display text-[18px] font-bold text-white leading-tight">{spread(c.bedtimeStdDev)}</p>
          </div>
          <div>
            <p className="text-[9px] font-body" style={{ color: "hsl(218 14% 58%)" }}>{t("sleep.consistency.wakeup")}</p>
            <p className="font-display text-[18px] font-bold text-white leading-tight">{spread(c.wakeupStdDev)}</p>
          </div>
        </div>
      ) : (
        <p className="text-[12px] font-body" style={{ color: "hsl(218 14% 65%)" }}>
          {t("sleep.consistency.needMore")}
        </p>
      )}

      <p className="text-[9px] font-body mt-2" style={{ color: "hsl(218 14% 48%)" }}>
        {tf("sleep.trend.basis", { nights: String(c.nights), days: String(c.days) })}
      </p>
      <p className="text-[9px] font-body mt-1 leading-snug" style={{ color: "hsl(218 14% 46%)" }}>
        {t("sleep.consistency.why")}
      </p>
    </motion.div>
  );
};

// ── 3 · TENDÊNCIA DE HRV ────────────────────────────────────────────────────

export const HrvTrendCard: React.FC<{ points: Array<{ t: number; v: number }>; now: number }> = ({ points, now }) => {
  const { t, tf } = useLanguage();
  const r = hrvTrend(points, 7, 21, now);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.1 }}
      className="rounded-2xl p-4 mb-4"
      style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 35% 6%))", border: `1px solid ${ACCENT}22` }}
    >
      <div className="flex items-center gap-1.5 mb-2">
        <Activity size={12} style={{ color: ACCENT }} />
        <p className="text-[9px] font-display tracking-[0.18em] uppercase" style={{ color: `${ACCENT}cc` }}>
          {t("sleep.hrvTrend.title")}
        </p>
      </div>

      {r.recentMedian === null ? (
        <p className="text-[12px] font-body" style={{ color: "hsl(218 14% 65%)" }}>
          {t("sleep.hrvTrend.noData")}
        </p>
      ) : (
        <div className="flex items-baseline gap-2">
          <span className="font-display text-[22px] font-bold text-white leading-none">{r.recentMedian}</span>
          <Delta pct={r.deltaPct} />
        </div>
      )}

      <p className="text-[9px] font-body mt-1.5" style={{ color: "hsl(218 14% 48%)" }}>
        {tf("sleep.hrvTrend.window", { recent: String(r.recentDays), baseline: String(r.baselineDays) })}
      </p>
    </motion.div>
  );
};
